// Country-specific tile visibility.
// Tiles (Crystallize Documents) can be tagged with topics under the "/countries/" topic map.
// A tile tagged with one or more country topics is only visible in those countries;
// a tile without any country topic is visible everywhere (default-visible).
//
// This module is isomorphic (used server-side when fetching page tiles and client-side
// in the tile renderers), so the prefix is a shared constant instead of the server-only
// CRYSTALLIZE_COUNTRY_TOPIC_PATH_PREFIX env variable.

export const COUNTRY_TOPIC_PATH_PREFIX = '/countries/';

interface TopicReference {
  path: string;
}

export interface CountryFilterableTile {
  __typename?: string;
  topics?: TopicReference[] | null;
}

export function isTileVisibleInCountry(tile: CountryFilterableTile, country: string): boolean {
  const countryTopicPaths = (tile.topics ?? [])
    .map((topic) => topic.path.toLowerCase())
    .filter((path) => path.startsWith(COUNTRY_TOPIC_PATH_PREFIX));

  // Tiles without country topics are visible everywhere; other topics (categories,
  // campaigns, ...) do not participate in country filtering.
  if (countryTopicPaths.length === 0) {
    return true;
  }

  const normalizedCountry = country.toLowerCase();

  return countryTopicPaths.some((path) => {
    // Compare only the first segment after the prefix, so sub-level topics
    // like "/countries/es/canary" still match country "es".
    const [topicCountry] = path.slice(COUNTRY_TOPIC_PATH_PREFIX.length).split('/');

    return topicCountry === normalizedCountry;
  });
}

// Whole Pages/PDPs reuse the same "/countries/<cc>" topic convention as tiles.
// A page tagged with one or more country topics is restricted to those countries:
// it must NOT render in any other country. This is the negation of the tile-visibility
// rule. If the country is unknown/empty we fail open (treat as not restricted) so that
// missing geo-IP data never hides otherwise public content.
export function isPathRestrictedForCountry(
  topics: TopicReference[] | null | undefined,
  country: string | null | undefined,
): boolean {
  if (!country) {
    return false;
  }

  return !isTileVisibleInCountry({ topics }, country);
}

export function filterTilesByCountry<T extends CountryFilterableTile>(
  tiles: T[],
  country: string,
): T[] {
  return tiles.filter((tile) => isTileVisibleInCountry(tile, country));
}

// Per-item display mode controlling how a country-restricted chooser item
// behaves: either it is hidden entirely, or it stays visible and the existing
// "not available" notice is surfaced when it is selected.
export type RestrictedDisplayMode = 'HIDE' | 'SHOW_MESSAGE';

// Backward-compatible default: untagged items keep the current behaviour
// (stay visible, surface the existing unavailability message).
export const DEFAULT_RESTRICTED_DISPLAY_MODE: RestrictedDisplayMode = 'SHOW_MESSAGE';

export interface CountryRestrictableItem extends CountryFilterableTile {
  displayMode?: RestrictedDisplayMode | null;
}

// Resolved visibility of a chooser item for a given country:
// - 'visible'                 -> render normally (no restriction applies)
// - 'hidden'                  -> remove from the chooser entirely
// - 'restricted-show-message' -> keep visible; selecting it must surface the
//                                existing "not available" notice
export type CountryVisibility = 'visible' | 'hidden' | 'restricted-show-message';

export function resolveCountryVisibility(
  item: CountryRestrictableItem,
  country: string | null | undefined,
): CountryVisibility {
  // Fail open when the country is unknown/empty so missing geo-IP data never
  // hides otherwise public content.
  if (!country) {
    return 'visible';
  }

  if (isTileVisibleInCountry(item, country)) {
    return 'visible';
  }

  const mode = item.displayMode ?? DEFAULT_RESTRICTED_DISPLAY_MODE;

  return mode === 'HIDE' ? 'hidden' : 'restricted-show-message';
}

// Drops items that resolve to 'hidden' for the given country. Items that are
// visible or restricted-with-message are kept (the message is surfaced
// elsewhere, on selection).
export function filterHiddenByCountry<T extends CountryRestrictableItem>(
  items: T[],
  country: string | null | undefined,
): T[] {
  return items.filter((item) => resolveCountryVisibility(item, country) !== 'hidden');
}

// True when the item is country-restricted but configured to stay visible and
// surface the existing unavailability message.
export function isCountryRestrictedWithMessage(
  item: CountryRestrictableItem,
  country: string | null | undefined,
): boolean {
  return resolveCountryVisibility(item, country) === 'restricted-show-message';
}

// True when the item is country-restricted and configured to be hidden entirely
// (i.e. dropped from the chooser by `filterHiddenByCountry`). The choosers use
// this to decide whether the current selection is still a rendered option: a
// 'visible' or 'restricted-show-message' selection stays put, only a hidden one
// warrants auto-snapping to the sole available option.
export function isHiddenByCountry(
  item: CountryRestrictableItem,
  country: string | null | undefined,
): boolean {
  return resolveCountryVisibility(item, country) === 'hidden';
}

// Returns the single item that is fully available ('visible') for the given
// country, iff EXACTLY ONE item resolves to 'visible'. Items resolving to
// 'hidden' or 'restricted-show-message' do NOT count toward availability, so a
// lone visible option alongside a show-message option still qualifies as the
// sole available option. Returns undefined when zero or more than one item is
// visible (including the fail-open case where no country is known and multiple
// items remain visible). Used by the choosers to auto-select the only choice.
export function getSoleAvailableOption<T extends CountryRestrictableItem>(
  items: T[],
  country: string | null | undefined,
): T | undefined {
  const visibleItems = items.filter(
    (item) => resolveCountryVisibility(item, country) === 'visible',
  );

  return visibleItems.length === 1 ? visibleItems[0] : undefined;
}

// Parses a free-form Crystallize selection value into a RestrictedDisplayMode.
// Accepts "hide"/"show_message" (case-insensitive); anything else (including
// undefined) falls back to the backward-compatible default.
export function parseRestrictedDisplayMode(
  value: string | null | undefined,
): RestrictedDisplayMode {
  return value?.toLowerCase() === 'hide' ? 'HIDE' : DEFAULT_RESTRICTED_DISPLAY_MODE;
}
